import React, { useState, useEffect } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import { Button, Card, Form, Alert, Row, Col } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import AsyncCreatableSelect from "react-select/async-creatable";
import { colors, constants, generateRoutePath, US_STATES } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";

const PageContainer = styled.div`
  max-width: 800px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.nightlyWoods700};
  text-decoration: none;
  margin-bottom: 16px;
  &:hover {
    color: ${colors.nightlyWoods900};
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 24px;
  color: ${colors.nightlyWoods900};
`;

const FormCard = styled(Card)`
  margin-bottom: 24px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 24px;
`;

export const SchoolCreate = () => {
  const { clientId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parentClient, setParentClient] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    domain: "",
    auth_method: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    nces_school_id: "",
    district_name: "",
    allowed_domains: "",
    curriculum_id: "",
    standards_framework_id: "",
  });
  const [curricula, setCurricula] = useState([]);
  const [frameworks, setFrameworks] = useState([]);

  useEffect(() => {
    const loadCurricula = async () => {
      try {
        const data = await request("curriculum", { method: "GET" });
        setCurricula(data || []);
      } catch {
        setCurricula([]);
      }
    };
    const loadFrameworks = async () => {
      try {
        const data = await request("knowledge_graph/frameworks", { method: "GET" });
        setFrameworks(data || []);
      } catch {
        setFrameworks([]);
      }
    };
    loadCurricula();
    loadFrameworks();
  }, []);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const client = await request(`admin/clients/${clientId}`, { method: "GET" });
        setParentClient(client);
      } catch (err) {
        console.error("Failed to fetch client:", err);
      }
    };
    fetchClient();
  }, [clientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const searchSchools = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      let url = `admin/schools/search?q=${encodeURIComponent(inputValue)}&limit=20`;
      if (parentClient?.nces_lea_id) {
        url += `&lea_id=${encodeURIComponent(parentClient.nces_lea_id)}`;
      }
      const response = await request(url, { method: "GET" });
      return response.results.map(school => ({
        value: school.id,
        label: `${school.name} (${school.city || ''}, ${school.state || ''})`,
        data: school,
      }));
    } catch (err) {
      console.error("Failed to search schools:", err);
      return [];
    }
  };

  const handleSchoolSelect = (option) => {
    setSelectedSchool(option);
    setInputValue("");
    if (option) {
      if (option.__isNew__) {
        setFormData(prev => ({
          ...prev,
          name: option.value,
          nces_school_id: "",
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          name: option.data.name,
          nces_school_id: option.data.nces_school_id || "",
          address: option.data.address || prev.address,
          city: option.data.city || prev.city,
          state: option.data.state || prev.state,
          zip_code: option.data.zip_code || prev.zip_code,
          district_name: option.data.district_name || prev.district_name,
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        name: "",
        nces_school_id: "",
      }));
    }
  };

  const handleInputChange = (newValue, { action }) => {
    if (action === "input-change") {
      setInputValue(newValue);
    }
    return newValue;
  };

  const handleBlur = () => {
    if (inputValue && !selectedSchool) {
      const newOption = {
        value: inputValue,
        label: inputValue,
        __isNew__: true,
      };
      setSelectedSchool(newOption);
      setFormData(prev => ({
        ...prev,
        name: inputValue,
        nces_school_id: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Name is required.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = { ...formData, client_id: clientId };
      Object.keys(payload).forEach(key => {
        if (payload[key] === "") payload[key] = null;
      });
      payload.name = formData.name.trim();
      if (formData.allowed_domains) {
        payload.allowed_domains = formData.allowed_domains
          .split(",")
          .map(d => d.trim())
          .filter(d => d);
      } else {
        payload.allowed_domains = null;
      }

      await request("admin/schools", {
        method: "POST",
        payload,
      });
      history.push(generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id: clientId }));
    } catch (err) {
      console.error("Failed to create school:", err);
      setError("Failed to create school. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push(generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id: clientId }));
  };

  const searchHelpText = parentClient?.nces_lea_id
    ? `Searching schools in district ${parentClient.nces_lea_id}`
    : "Search 102k schools. Auto-fills NCES ID, city & state.";

  return (
    <PageContainer>
      <BackLink to={generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id: clientId })}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Client
      </BackLink>

      <PageTitle>Add School</PageTitle>

      {error && <Alert variant="danger">{error}</Alert>}

      <FormCard>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <AsyncCreatableSelect
                cacheOptions
                loadOptions={searchSchools}
                onChange={handleSchoolSelect}
                onInputChange={handleInputChange}
                onBlur={handleBlur}
                inputValue={inputValue}
                value={selectedSchool}
                placeholder="Search or type school name..."
                isClearable
                allowCreateWhileLoading
                createOptionPosition="first"
                formatCreateLabel={(inputValue) => `Use "${inputValue}" as school name`}
                noOptionsMessage={({ inputValue }) =>
                  inputValue.length < 2
                    ? "Type 2+ chars to search"
                    : "No schools found - type to create"
                }
                formatOptionLabel={(option, { context }) => {
                  if (option.__isNew__) return option.label;
                  return context === "value" ? option.data?.name || option.label : option.label;
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: colors.nightlyWoods300,
                    minHeight: '38px',
                  }),
                }}
              />
              <Form.Text className="text-muted">
                {searchHelpText} Or type a custom name.
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Domain *</Form.Label>
                  <Form.Control
                    type="text"
                    name="domain"
                    value={formData.domain}
                    onChange={handleChange}
                    placeholder="e.g., school.org"
                  />
                  <Form.Text className="text-muted">
                    Used to verify teacher emails and filter students.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Auth Method *</Form.Label>
                  <Form.Select name="auth_method" value={formData.auth_method} onChange={handleChange}>
                    <option value="">Select...</option>
                    <option value="google">Google</option>
                    <option value="clever">Clever</option>
                    <option value="classlink">ClassLink</option>
                    <option value="microsoft">Microsoft</option>
                    <option value="none">None (Manual Name Entry)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    How students authenticate. "None" means students enter their name manually.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Additional Allowed Domains</Form.Label>
              <Form.Control
                type="text"
                name="allowed_domains"
                value={formData.allowed_domains}
                onChange={handleChange}
                placeholder="e.g., students.school.org, school.edu"
              />
              <Form.Text className="text-muted">
                Additional email domains allowed for students (comma-separated). The primary domain is always allowed.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Standards Framework</Form.Label>
              <Form.Select name="standards_framework_id" value={formData.standards_framework_id} onChange={handleChange}>
                <option value="">Common Core (CCSS)</option>
                {frameworks.map(f => (
                  <option key={f.identifier} value={f.identifier}>
                    {f.jurisdiction ? `${f.jurisdiction} — ${f.name || f.identifier}` : (f.name || f.identifier)}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                State standards framework used for question labeling. Defaults to Common Core if unset.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Curriculum</Form.Label>
              <Form.Select name="curriculum_id" value={formData.curriculum_id} onChange={handleChange}>
                <option value="">None</option>
                {curricula.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Pick a curriculum to enable the lesson picker. Leave as "None" and the school gets the default standards-based experience.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street address"
              />
            </Form.Group>

            <Row>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Select name="state" value={formData.state} onChange={handleChange}>
                    <option value="">Select...</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>ZIP Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>NCES School ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="nces_school_id"
                    value={formData.nces_school_id}
                    onChange={handleChange}
                    placeholder="12-digit NCES ID"
                    disabled={selectedSchool && !selectedSchool.__isNew__}
                  />
                  {selectedSchool && !selectedSchool.__isNew__ && (
                    <Form.Text className="text-success">Auto-filled from search</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>District Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="district_name"
                    value={formData.district_name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <ButtonGroup>
              <Button variant="outline-secondary" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                style={{ backgroundColor: colors.nightlyWoods500, borderColor: colors.nightlyWoods500 }}
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Create School"}
              </Button>
            </ButtonGroup>
          </Form>
        </Card.Body>
      </FormCard>
    </PageContainer>
  );
};
